/**
 * Experiments plugin: captures variant exposure metadata.
 */
import type { PluginContext } from "../lib/types";

import { registerPlugin } from "./register";

type Exposure = {
  experimentId?: string;
  variantId?: string;
  source: string;
};

const initializedExperimentDocuments = new WeakSet<Document>();

export function createExperimentsPlugin(ctx: PluginContext) {
  const seen = new Set<string>();
  let active = false;
  let wrappedDataLayer: any[] | undefined;
  let originalDataLayerPush: ((...args: any[]) => number) | undefined;
  let installedDataLayerPush: ((...args: any[]) => number) | undefined;

  const emitExposure = (payload: Exposure) => {
    if (!active) return;
    if (!ctx.canTrackCategory("experiment_exposure")) return;
    if (!payload.experimentId && !payload.variantId) return;

    const experimentId = payload.experimentId?.slice(0, 120);
    const variantId = payload.variantId?.slice(0, 120);
    const key = `${payload.source}:${experimentId || ""}:${variantId || ""}`;
    if (seen.has(key)) return;
    seen.add(key);

    ctx.track("experiment_exposure", {
      source: payload.source,
      experiment_id: experimentId,
      variant_id: variantId,
    });
  };

  const scanDom = () => {
    const nodes = document.querySelectorAll(
      "[data-exp-id], [data-exp-variant]",
    );
    nodes.forEach((node) => {
      const element = node as HTMLElement;
      emitExposure({
        source: "dom",
        experimentId: element.getAttribute("data-exp-id") || undefined,
        variantId: element.getAttribute("data-exp-variant") || undefined,
      });
    });
  };

  const scanGlobals = () => {
    const win = window as any;
    const raw = win.__EXPERIMENTS__;
    if (!raw || typeof raw !== "object") return;

    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      const variant =
        typeof value === "string"
          ? value
          : value && typeof value === "object" && "variant" in (value as any)
            ? String((value as any).variant)
            : undefined;
      emitExposure({
        source: "global",
        experimentId: key,
        variantId: variant,
      });
    });
  };

  const wrapDataLayer = () => {
    const win = window as any;
    const dataLayer = (win.dataLayer = win.dataLayer || []);
    if (!Array.isArray(dataLayer)) return;
    wrappedDataLayer = dataLayer;
    originalDataLayerPush = dataLayer.push;

    const process = (entry: Record<string, unknown>) => {
      if (!entry || typeof entry !== "object") return;
      const eventName = typeof entry.event === "string" ? entry.event : "";
      if (!eventName.includes("experiment") && !eventName.includes("variant")) {
        return;
      }
      const experimentId =
        (entry.experiment_id as string) ||
        (entry.experimentId as string) ||
        (entry.test_id as string) ||
        undefined;
      const variantId =
        (entry.variant_id as string) ||
        (entry.variantId as string) ||
        (entry.test_variant as string) ||
        undefined;
      emitExposure({ source: "datalayer", experimentId, variantId });
    };

    installedDataLayerPush = (...args: any[]) => {
      const result = originalDataLayerPush!.apply(dataLayer, args);
      if (!active) return result;
      args.forEach((entry) => {
        if (entry && typeof entry === "object") {
          process(entry as Record<string, unknown>);
        }
      });
      return result;
    };
    dataLayer.push = installedDataLayerPush;
  };

  return {
    onInit() {
      if (active) return;
      active = true;
      // A new instance after revoke must not reinterpret assignments that were
      // already present before the current consent epoch.
      if (!initializedExperimentDocuments.has(document)) {
        initializedExperimentDocuments.add(document);
        scanDom();
        scanGlobals();
      }
      wrapDataLayer();
    },
    onUnload() {
      if (!active) return;
      active = false;
      if (
        wrappedDataLayer &&
        installedDataLayerPush &&
        wrappedDataLayer.push === installedDataLayerPush &&
        originalDataLayerPush
      ) {
        wrappedDataLayer.push = originalDataLayerPush;
      }
      wrappedDataLayer = undefined;
      originalDataLayerPush = undefined;
      installedDataLayerPush = undefined;
      seen.clear();
    },
  };
}

registerPlugin("experiments", createExperimentsPlugin);
